import { Component, OnInit, OnDestroy, Input, Output, EventEmitter } from "@angular/core";
import { UntypedFormBuilder, UntypedFormGroup, Validators } from "@angular/forms";
import { DataProvider } from "projects/packcert-web/src/app/providers/data.provider";
import { SpinnerService, Message } from 'shared-components';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { AdminService } from "../../../services/admin.service";
import { AdminContextService } from "../../../context/admin-contex.service";

@Component({
    selector: 'app-mps-new-edit',
    templateUrl: './new-edit.component.html'
})
export class MpsNewEditComponent implements OnInit, OnDestroy {

    private _municipio;
    private _filters;

    public newEditForm: UntypedFormGroup;
    public showError: boolean;
    public showApiError: boolean; // evitar mostrar la clase de error en inputs si falla el getById
    public mensajes = new Array<Message>();

    public provincias;

    public disabled: Subject<boolean> = new Subject();

    private readonly destroy$ = new Subject<void>();

    @Input() municipioId;
    @Output() refresh = new EventEmitter<boolean>();
    @Output() mensaje = new EventEmitter<string>();


    constructor(
        private _fb: UntypedFormBuilder,
        private readonly _spinnerService: SpinnerService,
        private readonly _adminService: AdminService,
        private readonly _adminContext: AdminContextService,
        private readonly _dataProvider: DataProvider
    ) {}

    ngOnInit() {
        this.newEditForm = this._fb.group({
            des: ['', Validators.required],
            pais: ['', Validators.required],
            provincia: [{ value: '', disabled: true }, Validators.required],
            codigoPostal: [''],
            escala: ['']
        });

        this.newEditForm.get('pais').valueChanges.pipe(takeUntil(this.destroy$)).subscribe((pais) => {
            if (pais) {
                this._filters = {
                    paisId: pais.id
                }
                this._spinnerService.showSpinner();
                this._adminService.getProvinciasByPaisId(this._filters).toPromise().finally(() => {
                    this._spinnerService.hideSpinner();
                }).then((resp) => {
                    if (resp.lista.length > 0) {
                        this.provincias = resp.lista;
                        if (this.municipioId) {
                            this.newEditForm.get('provincia').patchValue(this.provincias.find(x => x.id === this._municipio.provinciaId))
                        }
                        this.disabled.next(false);
                        this.newEditForm.get('provincia').enable();
                    }
                });
            } else {
                this.provincias = [];
                this.disabled.next(true);
                this.newEditForm.get('provincia').patchValue('');
                this.newEditForm.get('provincia').disable();
            }
        });

        if (this.municipioId) {
            this.getMunicipio();
        }
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    public get paises() {
        if (this._dataProvider.paises) {
            if (this._dataProvider.paises.length === 1 && this.newEditForm.get('pais').enabled) {
              this.newEditForm.get('pais').patchValue(this._dataProvider.paises[0]);
              this.newEditForm.get('pais').disable();
            }
          }
        return this._dataProvider.paises;
    }

    public getMunicipio() {
        this._adminService.getMunicipio(this.municipioId).subscribe((resp) => {
            this._municipio = resp;
            this.newEditForm.patchValue({
                des: resp.des,
                pais: this.paises.find(x => x.id === resp.paisId),
                codigoPostal: resp.codigoPostal,
                escala: resp.escala
            });
            this.disabled.next(false);
            this.newEditForm.get('provincia').enable();
        }, (error) => {
            this.showApiError = true;
            if (error.status === 420) {
                this.mensajes.push({ class: 'error', message: 'global.error.noAccess' });
            } else {
                this.mensajes.push({ class: 'error', message: 'global.error.noResponse' });
            }
        });
    }

    public close() {
        this.refresh.emit(false);
    }

    public save() {
        this.checkForm();
        if (this.newEditForm.valid && !this.municipioId) {
            this._municipio = {
                des: this.newEditForm.get('des').value ? this.newEditForm.get('des').value : '',
                provinciaId: this.newEditForm.get('provincia') ? this.newEditForm.get('provincia').value.id : '',
                paisId: this.newEditForm.get('pais').value ? this.newEditForm.get('pais').value.id : '',
                codigoPostal: this.newEditForm.get('codigoPostal').value ? this.newEditForm.get('codigoPostal').value : '',
                escala: this.newEditForm.get('escala').value ? this.newEditForm.get('escala').value : 0,
            };
            this._spinnerService.showSpinner();
            this._adminService.createMunicipio(this._municipio).toPromise().finally(() => {
                this._spinnerService.hideSpinner();
            }).then((resp) => {
                if (resp.id) {
                    this.refresh.emit(true);
                    this.mensaje.emit('global.success.itemCreated');
                } else {
                    this.showError = true;
                    this.mensajes.push({ class: 'error', message: 'global.error.noResponse'});
                }
            });
        } else if (this.newEditForm.valid && this.municipioId) {
            this._municipio = {
                id: this.municipioId,
                des: this.newEditForm.get('des').value ? this.newEditForm.get('des').value : '',
                provinciaId: this.newEditForm.get('provincia') ? this.newEditForm.get('provincia').value.id : '',
                paisId: this.newEditForm.get('pais').value ? this.newEditForm.get('pais').value.id : '',
                codigoPostal: this.newEditForm.get('codigoPostal').value ? this.newEditForm.get('codigoPostal').value : '',
                escala: this.newEditForm.get('escala').value ? this.newEditForm.get('escala').value : 0,
            };
            this._spinnerService.showSpinner();
            this._adminService.updateMunicipio(this._municipio).toPromise().finally(() => {
                this._spinnerService.hideSpinner();
            }).then((resp) => {
                    this.refresh.emit(true);
                    this.mensaje.emit('global.success.itemUpdated');
                },
                (error) => {
                    this.showError = true;
                    this.mensajes.push({ class: 'error', message: 'global.error.noResponse' });
                }
            );
        }
    }

    private checkForm() {
        this.showError = this.newEditForm.invalid;
        if (this.showError) {
            this.mensajes.push({ class: 'error', message: 'global.error.required'});
        }
    }
}